import { AnchorProvider, Program, Wallet } from '@coral-xyz/anchor'
import { Connection, ConnectionConfig, PublicKey } from '@solana/web3.js'

import { IDL, ORDER_BOOK_IDL } from '@src/chains/solana/anchor/idl'
import { TokenMessengerMinter } from '@src/chains/solana/types/token_messenger_minter'
import { ANCHOR_PROVIDER_URL, ANCHOR_PROVIDER_URL_FALLBACK } from '@src/configs/env'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('SolanaSetup')

// Helius RPC endpoint
const HELIUS_RPC_URL = 'https://nora-fpasy7-fast-mainnet.helius-rpc.com/'

// Default connection configuration for high-priority transactions
const HIGH_PRIORITY_CONNECTION_CONFIG: ConnectionConfig = {
    commitment: 'processed',
    disableRetryOnRateLimit: false,
    confirmTransactionInitialTimeout: 120000, // 2 minutes
}

/**
 * Get a high-priority connection with enhanced fallback handling.
 * This creates a fresh connection each time it's called to avoid stale connections.
 *
 * @param forceFallback - If true, forces the use of the fallback URL even if the primary URL is available
 * @param useHelius - If true, uses the Helius RPC endpoint instead of the default endpoints
 * @returns A Connection instance configured for high-priority transactions
 */
export const getHighPriorityConnection = (forceFallback: boolean = false, useHelius: boolean = false) => {
    // Convert HTTPS URLs to WSS for WebSocket endpoints
    const primaryWss = ANCHOR_PROVIDER_URL?.replace('https', 'wss') || ''
    const fallbackWss = ANCHOR_PROVIDER_URL_FALLBACK?.replace('https', 'wss') || ''

    try {
        // If useHelius is true, use the Helius RPC endpoint regardless of other settings
        if (useHelius) {
            logger.debug('Using Helius RPC endpoint as requested')
            return new Connection(HELIUS_RPC_URL, {
                ...HIGH_PRIORITY_CONNECTION_CONFIG,
                wsEndpoint: HELIUS_RPC_URL.replace('https', 'wss'),
            })
        }

        // If forceFallback is true and fallback URL exists, skip primary URL
        if (forceFallback && ANCHOR_PROVIDER_URL_FALLBACK) {
            logger.debug('Forcing use of fallback RPC endpoint as requested')
            return new Connection(ANCHOR_PROVIDER_URL_FALLBACK, {
                ...HIGH_PRIORITY_CONNECTION_CONFIG,
                wsEndpoint: fallbackWss,
            })
        }

        if (!ANCHOR_PROVIDER_URL) {
            throw new Error('Primary RPC URL is not configured')
        }

        logger.debug('Creating high priority connection with primary endpoint')
        return new Connection(ANCHOR_PROVIDER_URL, {
            ...HIGH_PRIORITY_CONNECTION_CONFIG,
            wsEndpoint: primaryWss,
        })
    } catch (error) {
        logger.warn('Primary RPC connection failed, falling back to backup endpoint', {
            error: error instanceof Error ? error.message : String(error),
        })

        if (!ANCHOR_PROVIDER_URL_FALLBACK) {
            logger.error('No fallback RPC URL configured')
            throw new Error('Both primary and fallback RPC URLs are unavailable')
        }

        return new Connection(ANCHOR_PROVIDER_URL_FALLBACK, {
            ...HIGH_PRIORITY_CONNECTION_CONFIG,
            wsEndpoint: fallbackWss,
        })
    }
}

// Keep the original connection for other operations
export const connection = new Connection(ANCHOR_PROVIDER_URL || '', {
    commitment: 'confirmed',
    disableRetryOnRateLimit: false,
    confirmTransactionInitialTimeout: 120000, // Set timeout to 2 minutes
})
export const TOKEN_MESSENGER_MINTER_PROGRAM_ID = new PublicKey('CCTPiPYPc6AsJuwueEnWgSgucamXDZwBd53dQ11YiKX3')
export const MESSAGE_TRANSMITTER_PROGRAM_ID = new PublicKey('CCTPmbSD7gX1bxKPAmg77w8oFzNFpaQiQUWD43TKaecd')
export const ORDER_BOOK_PROGRAM_ID = new PublicKey('B32JUhPbfuQ4ai59DVLzpvSFqodr99YEpkovEx2XnKDK')

// Initialize the program interface with the IDL, program ID, and connection.
// This setup allows us to interact with the on-chain program using the defined interface.
export const program = new Program<TokenMessengerMinter>(IDL, TOKEN_MESSENGER_MINTER_PROGRAM_ID, {
    connection,
})

export const [tokenMessengerMinter] = PublicKey.findProgramAddressSync(
    [Buffer.from('token_messenger_minter')],
    program.programId
)

const getProvider = (connection: Connection, wallet: Wallet) => {
    return new AnchorProvider(connection, wallet, AnchorProvider.defaultOptions())
}

export const initializeProgram = (wallet: Wallet) => {
    const provider = getProvider(connection, wallet)
    return new Program(IDL, TOKEN_MESSENGER_MINTER_PROGRAM_ID, provider)
}

export const initializeOrderBookProgram = (wallet: Wallet) => {
    const provider = getProvider(connection, wallet)
    return new Program(ORDER_BOOK_IDL, ORDER_BOOK_PROGRAM_ID, provider)
}
